import { ThemeEvents } from '@theme/events';

const KACHING_SEL =
  'kaching-bundles, kaching-bundle, kaching-subscriptions, [class*="kaching-bundles"], [class*="kaching-subscription"], [class*="ks-card"]';
const BUNDLE_BAR_SEL =
  '.kaching-bundles__bar-container, .kaching-bundles__bar-main, [class*="kaching-bundles__bar-container"]';
const BUNDLE_SALE_SEL =
  '.kaching-bundles__bar-price, [class*="bar-price"]:not([class*="compare"]):not([class*="per-pack"])';
const FREQUENCY_CARD_SEL =
  '[class*="subscriptions_card"], [class*="subscription_card"], [class*="onetime"], [class*="one-time"], [class*="one_time"], .ks-card-wrapper, .ks-card-info, [class*="ks-card"]';
const KS_SALE_SEL =
  '.ks-discounted-price, [data-testid="discount-price"], [data-testid="discounted-price"]';
const KS_COMPARE_SEL =
  '.ks-original-price, .ks-compare-price, [data-testid="original-price"], [data-testid="compare-at-price"], [class*="compare-at"], [class*="compare_at"], [class*="original-price"]';
const KS_CARD_SEL = '.ks-card-wrapper, .ks-card-info, [class*="ks-card"]';

/** @type {WeakMap<HTMLElement, string>} */
let defaultHtml = new WeakMap();

/** @type {{ text: string, display: string } | null} */
let badgeDefault = null;

/** @type {HTMLElement | null} */
let sectionEl = null;

/** @type {boolean} */
let isSyncing = false;

/** @type {string | null} */
let lastSyncedKey = null;

/** @type {number | null} */
let lastSyncedPacks = null;

/** @type {ReturnType<typeof setTimeout> | null} */
let kachingObserverTimer = null;

/** @param {ParentNode} root @param {(root: ParentNode) => void} fn */
function walkRoots(root, fn) {
  fn(root);
  root.querySelectorAll?.('*').forEach((el) => {
    if (el.shadowRoot) walkRoots(el.shadowRoot, fn);
  });
}

/** @param {HTMLElement} el */
function cardText(el) {
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
}

/** @param {HTMLElement} el */
function isBundleDealCard(el) {
  return /buy\s+\d+\s+packs?/i.test(cardText(el));
}

/** @param {HTMLElement} el */
function isFrequencyCard(el) {
  const cls = typeof el.className === 'string' ? el.className : '';
  if (/subscriptions_card|subscription_card|onetime|one-time|one_time|ks-card/i.test(cls)) return true;
  const text = cardText(el);
  if (/buy\s+\d+\s+packs?/i.test(text) && !/subscribe|one[-\s]?time/i.test(text)) return false;
  return /subscribe\s+and\s+save|one[-\s]?time\s+purchase/i.test(text);
}

/** @param {HTMLInputElement} input */
function isBundleRadio(input) {
  const freqWrap = input.closest(FREQUENCY_CARD_SEL);
  if (freqWrap) return false;
  const wrap =
    input.closest(BUNDLE_BAR_SEL) || input.closest('[class*="kaching-bundles__bar"]');
  if (wrap instanceof HTMLElement && isFrequencyCard(wrap)) return false;
  if (input.name.includes('kaching-bundles-deal') || input.name.includes('kaching-bundles-bar')) {
    return !(wrap instanceof HTMLElement) || isBundleDealCard(wrap);
  }
  return wrap instanceof HTMLElement && isBundleDealCard(wrap);
}

/** @returns {HTMLElement | null} */
function getSelectedBundleBar() {
  /** @type {HTMLElement | null} */
  let bar = null;

  walkRoots(document, (root) => {
    if (bar) return;
    root.querySelectorAll?.('input[type="radio"]:checked').forEach((input) => {
      if (bar || !(input instanceof HTMLInputElement) || !isBundleRadio(input)) return;
      bar =
        input.closest(BUNDLE_BAR_SEL) ||
        input.closest('[class*="kaching-bundles__bar"]');
    });
  });

  if (bar instanceof HTMLElement) return bar;

  walkRoots(document, (root) => {
    if (bar) return;
    const selected = root.querySelector?.(
      '[class*="kaching-bundles__bar"][class*="selected"], .kaching-bundles__bar-container--selected'
    );
    if (selected instanceof HTMLElement) {
      bar = selected.closest(BUNDLE_BAR_SEL) || selected;
    }
  });

  return bar instanceof HTMLElement ? bar : null;
}

/** @returns {number} */
function getSelectedPackCount() {
  const bar = getSelectedBundleBar();
  if (bar instanceof HTMLElement) {
    const text = (bar.textContent || '').replace(/\s+/g, ' ');
    const buyMatch = text.match(/Buy\s+(\d+)\s+Packs?/i);
    if (buyMatch) return Math.max(1, Number.parseInt(buyMatch[1], 10) || 1);

    const titleEl = bar.querySelector(
      '[class*="bar-title"], [class*="deal-title"], [class*="title"]'
    );
    if (titleEl?.textContent) {
      const titleMatch = titleEl.textContent.match(/(\d+)\s*Packs?/i);
      if (titleMatch) return Math.max(1, Number.parseInt(titleMatch[1], 10) || 1);
    }

    const qtyAttr =
      bar.getAttribute('data-quantity') ||
      bar.getAttribute('data-qty') ||
      bar.querySelector('[data-quantity]')?.getAttribute('data-quantity');
    if (qtyAttr) {
      const n = Number.parseInt(qtyAttr, 10);
      if (n > 0) return n;
    }
  }

  /** @type {number | null} */
  let eventQty = null;
  walkRoots(document, (root) => {
    if (eventQty != null) return;
    const block = root.querySelector?.('kaching-bundles-block, kaching-bundles, kaching-bundle');
    if (!(block instanceof HTMLElement)) return;
    const qtyInput = block.querySelector?.(
      'input[name="quantity"], input[name*="quantity"], input[type="hidden"][name*="qty"]'
    );
    if (qtyInput instanceof HTMLInputElement && qtyInput.value) {
      const n = Number.parseInt(qtyInput.value, 10);
      if (n > 0) eventQty = n;
    }
  });
  if (eventQty != null) return eventQty;

  const formQty = document.querySelector(
    'form[action*="/cart/add"] input[name="quantity"], quantity-input input, .quantity__input'
  );
  if (formQty instanceof HTMLInputElement && formQty.value) {
    const n = Number.parseInt(formQty.value, 10);
    if (n > 0) return n;
  }

  return 1;
}

/**
 * Scale tablet/capsule counts and month-supply from the base (1-pack) metafield text.
 * @param {string} original
 * @param {number} packs
 */
function scaleSupplyText(original, packs) {
  const qty = Math.max(1, packs);
  return original
    .replace(/(\d+)\s*(Tablets?|Capsules?)/gi, (_, num, unit) => `${Number.parseInt(num, 10) * qty} ${unit}`)
    .replace(
      /(\d+)([-\u2013\u2014\s]*)Month\s*Supply/gi,
      (_, num, sep) => `${Number.parseInt(num, 10) * qty}${sep || '-'}Month Supply`
    );
}

/** @param {number} packs */
function syncProductTextSupply(packs) {
  document.querySelectorAll('[data-dcoded-bundle-supply]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;

    if (!el.dataset.dcodedOriginalText) {
      el.dataset.dcodedOriginalText = (el.textContent || '').replace(/\s+/g, ' ').trim();
    }

    const original = el.dataset.dcodedOriginalText;
    if (!original) return;

    const next = scaleSupplyText(original, packs);
    const target = el.querySelector('p, .metafield-rich_text_field, span') || el;
    const current = (target.textContent || '').replace(/\s+/g, ' ').trim();
    if (current !== next) {
      target.textContent = next;
    }
  });
}

/** @param {string} text */
function parseMoney(text) {
  const match = String(text).match(/[£$€]\s*[\d,.]+/);
  return match ? match[0].replace(/\s+/g, '') : '';
}

/** @param {string} text */
function moneyNum(text) {
  const cleaned = String(text).replace(/[^\d.,]/g, '');
  if (!cleaned) return 0;
  if (cleaned.includes('.') && cleaned.includes(',')) {
    return Number.parseFloat(cleaned.replace(/,/g, '')) || 0;
  }
  return Number.parseFloat(cleaned.replace(/,/g, '')) || 0;
}

/**
 * @param {HTMLElement} card
 * @returns {{ sale: string, compare: string | null } | null}
 */
function readCardTotalPrices(card) {
  /** @type {string[]} */
  const compareCandidates = [];
  card
    .querySelectorAll(
      `s, del, [class*="compare"], [class*="original"], [class*="strike"], ${KS_COMPARE_SEL}`
    )
    .forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (/per\s*pack/i.test(el.textContent || '')) return;
      const money = parseMoney(el.textContent || '');
      if (money) compareCandidates.push(money);
    });

  /** @type {string[]} */
  const saleCandidates = [];
  card.querySelectorAll(`${BUNDLE_SALE_SEL}, ${KS_SALE_SEL}`).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (/per\s*pack/i.test(el.textContent || '')) return;
    const money = parseMoney(el.textContent || '');
    if (money) saleCandidates.push(money);
  });

  const allMoney = [...(card.textContent || '').matchAll(/[£$€]\s*[\d,.]+(?!\s*per\s*pack)/gi)]
    .map((m) => m[0].replace(/\s+/g, ''))
    .filter((money, i, arr) => arr.indexOf(money) === i);

  let sale = saleCandidates[0] || '';
  let compare = compareCandidates.find((c) => c !== sale) || compareCandidates[0] || null;

  if (sale && !compare && allMoney.length > 1) {
    const other = allMoney
      .filter((m) => m !== sale)
      .sort((a, b) => moneyNum(b) - moneyNum(a))[0];
    if (other && moneyNum(other) > moneyNum(sale)) compare = other;
  }

  if (!sale && allMoney.length) {
    const unique = [...new Set(allMoney)];
    if (unique.length === 1) {
      sale = unique[0];
    } else {
      const sorted = unique.slice().sort((a, b) => moneyNum(a) - moneyNum(b));
      sale = sorted[0];
      compare = compare || sorted[sorted.length - 1];
    }
  }

  if (!sale) return null;
  if (compare && (compare === sale || moneyNum(compare) <= moneyNum(sale))) {
    compare = null;
  }

  return { sale, compare };
}

/** @returns {HTMLElement | null} */
function getSelectedFrequencyCard() {
  /** @type {HTMLElement | null} */
  let checkedCard = null;
  /** @type {HTMLElement | null} */
  let classCard = null;

  walkRoots(document, (root) => {
    root.querySelectorAll?.(FREQUENCY_CARD_SEL).forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.querySelector('input[type="radio"]:checked')) {
        if (!checkedCard) checkedCard = el;
        return;
      }
      const cls = typeof el.className === 'string' ? el.className : '';
      if (el.classList.contains('selected') || /(?:^|\s)[\w-]*selected[\w-]*(?:\s|$)/i.test(cls)) {
        if (!classCard) classCard = el;
      }
    });
  });

  if (checkedCard) return checkedCard;
  if (classCard) return classCard;

  walkRoots(document, (root) => {
    if (checkedCard) return;
    root.querySelectorAll?.('input[type="radio"]:checked').forEach((input) => {
      if (checkedCard || !(input instanceof HTMLInputElement) || isBundleRadio(input)) return;
      if (!input.closest('[class*="kaching"], [class*="ks-"]')) return;
      const wrap =
        input.closest(FREQUENCY_CARD_SEL) ||
        input.closest(BUNDLE_BAR_SEL) ||
        input.closest(KS_CARD_SEL);
      if (wrap instanceof HTMLElement && (isFrequencyCard(wrap) || !isBundleDealCard(wrap))) {
        checkedCard = wrap;
      }
    });
  });

  return checkedCard instanceof HTMLElement ? checkedCard : null;
}

/** @returns {{ sale: string, compare: string | null } | null} */
function readActivePrices() {
  const frequencyCard = getSelectedFrequencyCard();
  if (frequencyCard) {
    const prices = readCardTotalPrices(frequencyCard);
    if (prices) return prices;
  }

  const bundleBar = getSelectedBundleBar();
  if (bundleBar && !isFrequencyCard(bundleBar)) {
    const prices = readCardTotalPrices(bundleBar);
    if (prices) return prices;
  }

  return null;
}

/** @param {HTMLElement | null} section */
function getTargets(section) {
  /** @type {HTMLElement[]} */
  const targets = [];
  const seen = new Set();

  const add = (el) => {
    if (!(el instanceof HTMLElement) || seen.has(el)) return;
    if (el.closest('product-card, .product-card, .predictive-search')) return;
    seen.add(el);
    targets.push(el);
  };

  section?.querySelectorAll?.('product-price [ref="priceContainer"]').forEach(add);
  document
    .querySelectorAll(
      '.dcoded-pdp-product-information product-price [ref="priceContainer"]'
    )
    .forEach(add);

  return targets;
}

/** @param {HTMLElement} el */
function cacheDefault(el) {
  if (!defaultHtml.has(el)) defaultHtml.set(el, el.innerHTML);
}

/** @param {HTMLElement} el */
function restoreDefault(el) {
  const html = defaultHtml.get(el);
  if (html && el.innerHTML !== html) el.innerHTML = html;
}

/** @param {HTMLElement} el */
function getOriginalCompare(el) {
  cacheDefault(el);
  const temp = document.createElement('div');
  temp.innerHTML = defaultHtml.get(el) || el.innerHTML;
  return temp.querySelector('.compare-at-price')?.textContent?.trim() || null;
}

/** @param {HTMLElement} el */
function getCurrentSale(el) {
  return el.querySelector('.price')?.textContent?.trim() || null;
}

/** @param {HTMLElement} el @param {string} sale @param {string | null} compare */
function applyPrices(el, sale, compare) {
  const currentSale = getCurrentSale(el);
  const currentCompare = el.querySelector('.compare-at-price')?.textContent?.trim() || null;
  if (currentSale === sale && currentCompare === (compare || null)) return;

  cacheDefault(el);

  if (compare && moneyNum(compare) > moneyNum(sale)) {
    el.innerHTML = `
      <span role="group"><span class="visually-hidden">Sale price</span><span class="price">${sale}</span></span>
      <span role="group"><span class="visually-hidden">Regular price</span><span class="compare-at-price">${compare}</span></span>`;
    return;
  }

  el.innerHTML = `<span class="price">${sale}</span>`;
}

/** @param {HTMLElement} section @param {string} sale @param {string | null} compare */
function applyBadge(section, sale, compare) {
  const badge = section.querySelector('.price-saving-badge');
  if (!(badge instanceof HTMLElement)) return;

  if (!badgeDefault) {
    badgeDefault = { text: badge.textContent, display: badge.style.display };
  }

  if (!compare) {
    if (badge.textContent !== badgeDefault.text) badge.textContent = badgeDefault.text;
    badge.style.display = badgeDefault.display;
    return;
  }

  const saleNum = moneyNum(sale);
  const compareNum = moneyNum(compare);

  if (!saleNum || !compareNum || compareNum <= saleNum) {
    if (badge.textContent !== badgeDefault.text) badge.textContent = badgeDefault.text;
    badge.style.display = badgeDefault.display;
    return;
  }

  const nextText = `Save ${Math.round((1 - saleNum / compareNum) * 100)}%`;
  if (badge.textContent !== nextText) badge.textContent = nextText;
  badge.style.removeProperty('display');
}

/** @param {string} saleText */
function currencySymbol(saleText) {
  const match = String(saleText || '').match(/[£$€]/);
  return match ? match[0] : '£';
}

/** @param {number} value @param {string} saleText */
function formatMoneyFromSale(value, saleText) {
  return `${currencySymbol(saleText)}${value.toFixed(2)}`;
}

/**
 * @param {HTMLElement} section
 * @param {string} sale
 */
function syncPerDayMonthly(section, sale) {
  const priceEl = section.querySelector('product-price[data-capsules-per-pack]');
  const monthlyRoot =
    section.querySelector('[data-dcoded-monthly-supply][data-capsules-per-pack]') ||
    section.querySelector('[data-dcoded-monthly-supply]');

  const capsSource = monthlyRoot || priceEl;
  if (!(capsSource instanceof HTMLElement)) return;

  const perPack =
    Number.parseInt(capsSource.getAttribute('data-capsules-per-pack') || priceEl?.getAttribute('data-capsules-per-pack') || '90', 10) ||
    90;
  const packs = getSelectedPackCount() || 1;
  const caps = Math.max(1, perPack * packs);
  const months = caps / 30;
  const live = moneyNum(sale);
  if (!live || !caps) return;

  const pence = (live / caps) * 100;
  const perDayText = `${pence < 10 ? pence.toFixed(1) : Math.round(pence)}p`;
  const monthlyText = `${formatMoneyFromSale(live / months, sale)} a month`;
  const monthsLabel = Number.isInteger(months) ? String(months) : String(Math.round(months));
  const monthlyTail = `over your ${monthsLabel}-month supply`;

  if (priceEl instanceof HTMLElement) {
    const perDayEl = priceEl.querySelector('[data-dcoded-perday]');
    if (perDayEl && perDayEl.textContent !== perDayText) perDayEl.textContent = perDayText;
  }

  const monthlyScope = monthlyRoot instanceof HTMLElement ? monthlyRoot : section;
  const monthlyEl = monthlyScope.querySelector('[data-dcoded-monthly]');
  if (monthlyEl && monthlyEl.textContent !== monthlyText) monthlyEl.textContent = monthlyText;

  const monthlyTailEl = monthlyScope.querySelector('[data-dcoded-monthly-tail]');
  if (monthlyTailEl && monthlyTailEl.textContent !== monthlyTail) monthlyTailEl.textContent = monthlyTail;
}

/** @param {HTMLElement} section */
function restoreBadge(section) {
  const badge = section.querySelector('.price-saving-badge');
  if (!(badge instanceof HTMLElement) || !badgeDefault) return;
  badge.textContent = badgeDefault.text;
  badge.style.display = badgeDefault.display;
}

/** @param {string} text */
function moneyToCents(text) {
  const n = Number.parseFloat(String(text || '').replace(/[^\d.]/g, ''));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

/** @param {{ sale: string, compare: string | null }} prices */
function storeKachingCompare(prices) {
  const packs = getSelectedPackCount() || 1;
  const cents = moneyToCents(prices?.compare);
  const productId =
    document.querySelector('product-form-component')?.getAttribute('data-product-id') ||
    document.querySelector('[data-product-id]')?.getAttribute('data-product-id') ||
    '';

  window.__dcodedKachingCompare = { cents, qty: packs, productId, compare: prices?.compare || '' };

  if (cents > 0 && productId) {
    try {
      sessionStorage.setItem(`dcoded-kaching-compare:${productId}:${packs}`, String(cents));
      sessionStorage.setItem(`dcoded-kaching-compare:${productId}`, String(cents));
    } catch {
      /* ignore */
    }
  }

  document.querySelectorAll('product-form-component form, form[action*="/cart/add"]').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    const ensure = (name, value) => {
      let input = form.querySelector(`input[name="${name}"]`);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    };
    if (cents > 0) {
      ensure('properties[_kaching_compare]', String(cents));
      ensure('properties[_kaching_compare_qty]', String(packs));
    }
  });
}

/** @param {HTMLElement} section @param {{ sale: string, compare: string | null } | null} prices */
function syncPrices(section, prices) {
  const targets = getTargets(section);
  targets.forEach(cacheDefault);

  if (!prices?.sale) {
    lastSyncedKey = null;
    targets.forEach(restoreDefault);
    restoreBadge(section);
    return;
  }

  targets.forEach((el) => applyPrices(el, prices.sale, prices.compare));
  applyBadge(section, prices.sale, prices.compare);
  syncPerDayMonthly(section, prices.sale);
  lastSyncedKey = `${prices.sale}|${prices.compare || ''}`;
  storeKachingCompare(prices);
}

/** @returns {HTMLElement | null} */
function findSection() {
  return (
    document.querySelector('.dcoded-pdp-sticky')?.closest('.shopify-section') ||
    document.querySelector('.shopify-section:has([id^="ProductInformation-"])') ||
    document.querySelector('.shopify-section:has(.product-details product-price)')
  );
}

/** @param {EventTarget | null} target */
function isKachingInteraction(target) {
  return (
    target instanceof Element &&
    !!target.closest(`${KACHING_SEL}, [class*="kaching-bundles"], [class*="kaching-subscription"], [class*="ks-"]`)
  );
}

function runSync() {
  if (!(sectionEl instanceof HTMLElement) || isSyncing) return;

  const packs = getSelectedPackCount();
  if (packs !== lastSyncedPacks) {
    syncProductTextSupply(packs);
    lastSyncedPacks = packs;
  }

  const prices = readActivePrices();
  const nextKey = prices?.sale ? `${prices.sale}|${prices.compare || ''}` : '';
  if (nextKey === lastSyncedKey) return;

  isSyncing = true;
  try {
    syncPrices(sectionEl, prices);
  } finally {
    isSyncing = false;
  }
}

function scheduleKachingSync() {
  if (kachingObserverTimer) clearTimeout(kachingObserverTimer);
  kachingObserverTimer = setTimeout(runSync, 80);
}

function watchKachingReady() {
  runSync();
  requestAnimationFrame(runSync);
  [200, 500, 1000, 2000].forEach((ms) => setTimeout(runSync, ms));

  const observer = new MutationObserver(() => {
    if (isSyncing) return;
    scheduleKachingSync();
  });

  walkRoots(document, (root) => {
    root.querySelectorAll?.(KACHING_SEL).forEach((widget) => {
      if (widget instanceof Node) {
        observer.observe(widget, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'checked', 'aria-checked'],
        });
      }
    });
  });

  document.querySelectorAll('.dcoded-pdp-purchase-card').forEach((card) => {
    observer.observe(card, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'checked', 'aria-checked'],
    });
  });
}

function init() {
  sectionEl = findSection();
  if (!(sectionEl instanceof HTMLElement)) return;

  const badge = sectionEl.querySelector('.price-saving-badge');
  if (badge instanceof HTMLElement) {
    badgeDefault = { text: badge.textContent, display: badge.style.display };
  }

  getTargets(sectionEl).forEach(cacheDefault);

  const onInteraction = (event) => {
    if (!isKachingInteraction(event.target)) return;
    lastSyncedKey = null;
    lastSyncedPacks = null;
    requestAnimationFrame(runSync);
  };

  document.addEventListener('click', onInteraction, true);
  document.addEventListener('change', onInteraction, true);
  document.addEventListener('input', onInteraction, true);

  const onKachingDeal = () => {
    lastSyncedKey = null;
    lastSyncedPacks = null;
    requestAnimationFrame(runSync);
  };

  walkRoots(document, (root) => {
    root.querySelectorAll?.('kaching-bundles-block, kaching-bundles, kaching-bundle').forEach((block) => {
      block.addEventListener?.('deal-bar-selected', onKachingDeal);
      block.addEventListener?.('variants-changed', onKachingDeal);
      block.addEventListener?.('variant-selected', onKachingDeal);
    });
  });

  sectionEl.addEventListener(ThemeEvents.variantUpdate, () => {
    defaultHtml = new WeakMap();
    badgeDefault = null;
    lastSyncedKey = null;
    lastSyncedPacks = null;

    document.querySelectorAll('[data-dcoded-bundle-supply]').forEach((el) => {
      if (el instanceof HTMLElement) delete el.dataset.dcodedOriginalText;
    });

    requestAnimationFrame(() => {
      const badge = sectionEl?.querySelector('.price-saving-badge');
      if (badge instanceof HTMLElement) {
        badgeDefault = { text: badge.textContent, display: badge.style.display };
      }
      if (sectionEl) getTargets(sectionEl).forEach(cacheDefault);
      runSync();
    });
  });

  watchKachingReady();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
